/**
 * Domaine souhaité par le client — helpers de la modale « Enregistrer la demande » (étape 2).
 *
 * Le nom et l'extension sont **saisis séparément** (règle anti « monsite.fr.fr », la même que
 * `DomainSuggestionsManager`) et ne sont concaténés qu'au moment de l'envoi, sous la forme
 * `domainWish: "monsite.fr"` — exactement le format produit par le formulaire du portail
 * (`normalizeDomainWishInput`) et relu par `RequestInfo` (« Adresse web souhaitée »),
 * `DomainSuggestionsManager` (« Souhait du client ») et `LaunchDomainPanel` côté client.
 *
 * ⚠️ Rien ici n'est bloquant côté serveur : `domainWish` est facultatif et
 * `construireFormulaire` (msd_portail/functions/prospection.js) ne fait qu'un `trim`. Ce module
 * sert uniquement à empêcher une saisie impossible à réserver (espaces, accents, majuscules,
 * URL collée avec son protocole).
 */

/** Extensions réellement commercialisées — miroir du select de `DomainSuggestionsManager`. */
export const EXTENSIONS_DOMAINE = ['fr', 'com'];

/** Extension proposée par défaut : le marché visé est français. */
export const EXTENSION_PAR_DEFAUT = 'fr';

/** Longueur minimale d'un nom réservable (`.fr` et `.com` acceptent 2 caractères). */
export const LONGUEUR_MIN_NOM_DOMAINE = 2;

/** Extension tapée dans le champ (« monsite.com » collé d'un mail, par exemple). */
const EXTENSION_TAPEE_RE = /\.(fr|com)$/;

/**
 * Nettoie un nom de domaine : accents, majuscules, protocole, « www. », espaces, underscores,
 * points et tout caractère non réservable. Le champ ne portant que le **nom**, l'extension est
 * retirée (elle vit dans le select).
 *
 * @param {string} valeur - Saisie brute de l'agent
 * @return {string} Nom normalisé (63 caractères maximum, sans tiret en bord)
 */
export const normaliserNomDomaine = (valeur) =>
  String(valeur || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\./g, '')
    .replace(/[\s_]+/g, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

/**
 * Découpe une saisie libre en nom + extension. Si l'agent colle « monsite.com » ou une URL
 * complète, l'extension est reconnue et peut alimenter le select — le nom n'en garde pas la trace.
 *
 * @param {string} valeur - Saisie brute
 * @return {{name: string, extension: string|null}} Extension `null` quand aucune n'a été tapée
 */
export const decouperSaisieDomaine = (valeur) => {
  const brut = String(valeur || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\s+/g, '');

  const trouve = brut.match(EXTENSION_TAPEE_RE);
  const extension = trouve ? trouve[1] : null;
  const nom = normaliserNomDomaine(trouve ? brut.slice(0, -trouve[0].length) : brut);

  return { name: nom, extension };
};

/**
 * Nom complet à écrire dans `creation_form.domainWish`. Chaîne vide quand l'agent n'a rien
 * saisi — c'est ce vide qui rend le champ facultatif de bout en bout.
 *
 * @param {{name?: string, extension?: string}} parts - Parties séparées
 * @return {string} Par exemple « monsite.fr », ou `''`
 */
export const assemblerDomaine = (parts = {}) => {
  const nom = normaliserNomDomaine(parts.name);
  if (!nom) return '';
  const extension = String(parts.extension || EXTENSION_PAR_DEFAUT)
    .replace(/^\./, '')
    .trim()
    .toLowerCase();
  return extension ? `${nom}.${extension}` : nom;
};

/**
 * Le nom saisi est-il réservable ? Un champ vide reste valide (champ facultatif).
 *
 * @param {string} nom - Nom déjà normalisé
 * @return {boolean} Vrai si l'on peut tenter une réservation
 */
export const estNomDomaineValide = (nom) => {
  const texte = String(nom || '').trim();
  if (!texte) return true;
  return texte.length >= LONGUEUR_MIN_NOM_DOMAINE;
};
